/**
 * DocumentItemsViewElement — Dokumenty: linie dokumentu (increment 1) + układ kolumn tabeli pozycji.
 *
 * Klocek palety buildera reprezentujący sekcję pozycji dokumentu
 * (`document_items_view`, patrz `RecursiveFormRenderer` PLACEHOLDER_TYPES).
 * Na REALNEJ stronie dokumentu ten typ jest dispatchowany WYŁĄCZNIE przez
 * `contextRenderers['document_items_view']` (→ `DocumentItemsEditor`/
 * `DocumentItemsSection`) — ten rejestr obsługuje TYLKO podgląd w canvasie
 * buildera (mode='builder'), gdzie nie ma prawdziwego rekordu do wyświetlenia
 * (mirror innych placeholder-typów).
 *
 * `allowedModules` scopinguje ten klocek do 7 modułów klasy Dokumenty — lista
 * slugów mirroruje `DOCUMENT_MODULE_SLUGS`
 * (`apps/admin/src/pages/system-modules/document-module-slugs.ts`) — trzymana
 * lokalnie (nie importowana), żeby uniknąć zależności components→pages;
 * źródło prawdy pozostaje `apps/api/lib/seed-system-modules.ts`.
 *
 * Płótno buildera (`mode='builder'`) pokazuje edytor układu kolumn
 * (`DocumentItemsColumnsCanvas` → `TableLayoutEditor`), który zapisuje `settings.tableColumns` /
 * `settings.hiddenColumnIds` (kolejność, widoczność, szerokość, podpis, wyrównanie, miejsca po
 * przecinku). Jeden zapisany układ obowiązuje i widok odczytu (`DocumentItemsSection`), i edycję
 * pozycji (`DocumentItemsEditor`); gdy tych kluczy brak, oba czytają starszy `columnConfig`
 * (czytnik w `document-items-descriptors`). Osobnego panelu ustawień klocek nie ma — wszystko
 * robi edytor na płótnie, więc `defaultSettings` to pusty obiekt (bez zapisu = naturalna
 * kolejność, wszystkie kolumny widoczne).
 *
 * `render` jest wołany jak ZWYKŁA FUNKCJA (nie komponent) — patrz `LayoutElementRegistry` — więc
 * żaden hook nie może być wywołany BEZPOŚREDNIO w jego ciele (ten sam powód co w
 * `StatCardGroupElement`: brak `useTranslation`, standalone `i18n.t` zamiast). Hooki żyją
 * w zamontowanym komponencie-dziecku (`DocumentItemsColumnsCanvas`).
 */

import { List } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';

import { DocumentItemsColumnsCanvas } from './DocumentItemsColumnsCanvas';

/** Mirror `DOCUMENT_MODULE_SLUGS` — patrz komentarz nagłówkowy powyżej. */
const DOCUMENT_MODULE_SLUGS_FOR_ITEMS_VIEW = [
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'specjalne',
];

export const DocumentItemsViewElement: LayoutElementDefinition = {
  type: 'document_items_view',
  category: 'tresc',
  isContainer: false,
  label: 'Pozycje dokumentu',
  labelKey: 'tenant.documents.items.paletteLabel',
  icon: 'List',
  allowedModules: DOCUMENT_MODULE_SLUGS_FOR_ITEMS_VIEW,
  // Jeden na layout — nakładka per-tenant (ukryte kolumny) jest kluczowana TYPEM elementu.
  layoutSingleton: true,
  // Edytor kolumn na płótnie jest szeroką tabelą — cały wiersz gridu sekcji, nie pół komórki.
  fullWidth: true,
  defaultSettings: () => ({}),
  // Płótno kreatora = edytor układu kolumn (komponent-dziecko niesie hooki — ta funkcja jest
  // wołana jak zwykła); poza kreatorem zostaje placeholder — realną stronę dokumentu obsługuje
  // `contextRenderers`.
  render: ({ element, mode, onSettingsChange, systemHideOnly }) =>
    mode === 'builder' ? (
      <DocumentItemsColumnsCanvas
        element={element}
        onSettingsChange={onSettingsChange}
        systemHideOnly={systemHideOnly === true}
      />
    ) : (
      <div
        className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
        data-testid={`document-items-view-element-${element.id}`}
        data-element-type="document_items_view"
      >
        <List className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
        <p className="mt-1 text-xs font-medium text-text-secondary">
          {i18n.t('tenant.documents.items.paletteLabel', { defaultValue: 'Pozycje dokumentu' })}
        </p>
      </div>
    ),
};
